<template lang="pug">
.login(v-if="!isUserLoged && !isPSCLogin && !isFCLogin &!anonymous" :class="{'show': show}")
  .login__left
    img.logo(src="~/assets/images/EPOCA_logo.png" alt="Epoca")
    img.logo--text(src="~/assets/images/EPOCA_logo_text.png" alt="Epoca")
    video.login__video(
      autoplay 
      muted 
      loop 
      playsinline 
      poster="https://res.cloudinary.com/epoca/video/upload/v1751388379/happy-chat.jpeg"
    )
      source(src="https://res.cloudinary.com/epoca/video/upload/v1751388379/happy-chat.mp4" type="video/mp4")
  .login__right
    .login-holder
      .login-options
        .login-practitioner(v-if="currentLoginType=='changepassword'")
          LoginPasswordReset(:isForgotPassword="false")
        .login-practitioner(v-if="currentLoginType=='passwordreset'")
          LoginPasswordReset(:isForgotPassword="true")
        .login-practitioner(v-if="currentLoginType=='forgetpassword'")
          LoginForgetPassword
        .login-practitioner(v-if="currentLoginType=='emergencylogin'")
          LoginEmergency
        .login-practitioner(v-else-if="currentLoginType=='practitioner'")
          LoginEmailPassword(@on:show-psc="isPSCShow = $event")
          .splitter(v-if="appConfig?.data?.is_psc && isPSCShow") 
            .line
            span.line--label OU
          LoginPsc.psc(v-if="appConfig?.data?.is_psc && isPSCShow")
    .footer 
      v-btn.revoke.mt-2(variant="text" color="#868787" size="x-small" @click="openLink('https://epoca-sante.atlassian.net/servicedesk/customer/portal/3/topic/fd64d794-7c07-4edb-a3f1-468a91b7e582/article/2061697029')") CGU
      v-btn.revoke.mt-2(variant="text" color="#868787"  size="x-small" @click="revokeMessage()") Révoquer mon accès
      v-btn.revoke.mt-2(variant="text" color="#868787"  size="x-small" @click="navigateTo('/emergency-login')") 
        v-icon(right) epoca:signIn
</template>

<script setup lang="ts">
const { $api: api, $text: text } = useNuxtApp()
const userStore = useUserStore()
const navigationStore = useNavigationStore()
const isPSCLogin = ref(false)
const isFCLogin = ref(false)
const isAnonymousPossible = ref(false)
const route = useRoute()
const { auth } = useAuth()
const show = ref(false)
const isPSCShow = ref(true)
const appConfig = ref({})

const isUserLoged = computed(() => {
  return userStore.isUserLoged && !['/change-password', '/reset-password'].includes(route.path)
})

const anonymous = computed(() => {
  return navigationStore.anonymous
})

const props = defineProps({
  authType: {
    type: String,
    default: () => null,
  },
})

const currentLoginType = ref(props.authType)

const openLink = (link: string) => {
  navigateTo(link, {
    external: true,
    open: {
      target: '_blank',
    },
  })
}

const revokeMessage = async () => {
  const emailAddress: string = 'support@epoca.health'
  const ccEmailAddres: string = 'dpo@epoca.health'
  const subject: string =
    "Demande de révocation de compte suite à une compromission de mon moyen d'identification électronique"
  const body: string = `Madame, Monsieur,\nJe vous écris le présent mail pour vous informer de la compromission d’un de mes moyens d'identification électronique associés à mon compte dans l’application EIIS, développée par EPOCA.\nLe moyen d’identification électronique est celui qui concerne mon numéro de téléphone : [votre numéro de téléphone].\nJ’ai découvert la compromission lorsque [expliquez comment vous avez découvert la compromission, par exemple, une activité inhabituelle sur le compte, etc.].\nConformément aux CGU de la plateforme, je sollicite respectueusement la révocation immédiate de mon compte dans un délai de 15 jours afin de protéger mes informations personnelles ainsi que celles des patients sous ma prise en charge et de prévenir tout accès non autorisé à mon compte. Je comprends que cette mesure peut entraîner la suspension temporaire de mes services via ce moyen d’identification, mais qu’elle permet de garantir la sécurité de mon compte et de mes données, ainsi que celles concernant mes patients pris en charge. Je suis aussi conscient(e) que si j’ai une carte eCPS, l’usage de Pro Santé Connect est un moyen d’identification alternatif possible pour me connecter à l’application.\nJe suis prêt(e) à coopérer pleinement avec votre équipe pour faciliter le processus de révocation et de sécurisation de mon compte.\nVous pouvez me contacter à l'adresse e-mail [votre adresse e-mail] ou au numéro de téléphone [votre numéro de téléphone] pour toute information supplémentaire.\nCordialement,[NOM] [Prénom]`

  window.location.href = `mailto:${emailAddress}?cc=${ccEmailAddres}&subject=${subject}&body=${encodeURIComponent(body)}`
}
const goAnonymous = async () => {
  navigationStore.setAnonymous()
  navigateTo(navigationStore.redirection)
}

onMounted(async () => {
  appConfig.value = await api.cms.getConfig()
  show.value = true
  if (!props.authType)
    currentLoginType.value =
      (route.query['login-type'] && route.query['login-type'] == 'patient') || isAnonymousPossible.value
        ? 'patient'
        : 'practitioner'
  checkIfPSC(route.path)
  checkIfFC(route.path)
  const rememberedUsers = JSON.parse(localStorage.getItem('rememberUser') || '[]')
  if (rememberedUsers.length > 0) {
    let isRPPS = rememberedUsers.some((user: any) => user.type === 'RPPS')
    if (isRPPS) {
      isPSCShow.value = true
    } else {
      isPSCShow.value = false
    }
  } else {
    isPSCShow.value = true
  }
})

const checkIfPSC = (path: string) => {
  if (path === '/auth/psc/') {
    isPSCLogin.value = true
  } else {
    isPSCLogin.value = false
  }
}

const checkIfFC = (path: string) => {
  if (path === '/auth/login/') {
    isFCLogin.value = true
  } else {
    isFCLogin.value = false
  }
}

watch(
  () => route.path,
  (newValue, oldValue) => {
    checkIfPSC(newValue)
    isAnonymousPossible.value = navigationStore.redirection && navigationStore.redirection.indexOf('/call/external/') != -1
    if (!props.authType)
      currentLoginType.value =
        (route.query['login-type'] && route.query['login-type'] == 'patient') || isAnonymousPossible.value
          ? 'patient'
          : 'practitioner'
  },
)

watch(
  () => userStore.isUserLoged,
  async (newValue, oldValue) => {
    if (newValue === false) {
      if (navigationStore.wasPatient) {
        navigationStore.resetWasPatient()
        navigateTo('/?login-type=patient')
      } else {
        navigateTo('/')
      }
    } else if (newValue === true && navigationStore.redirection) {
      if (userStore.isTokenExpire) {
        window.location.replace('/')
        return
      }
      window.location.replace(navigationStore.redirection)
    } else if (newValue === true && !navigationStore.redirection) {
      if (userStore.isTokenExpire) {
        window.location.replace('/')
        return
      }
      if (userStore.user.isPatient) {
        // navigateTo('/user')
        window.location.replace('/user')
      } else {
        // navigateTo('/patients')
        window.location.replace('/patients')
      }
    }
  },
)
</script>

<style lang="stylus" scoped>
.splitter
  display: flex
  position: relative;
  align-items: center;
  flex-direction: column;
  .line
    height 1px
    background-color var(--Text-2, #C9C9C9);
    position: absolute;
    top: 10px;
    left: 0;
    right: 0;
    &--label
      color: var(--Text-2, #C9C9C9);
      body-b3-medium()
      line-height 20px
      background-color: var(--white, #FFFFFF);
  span
    display: flex
    z-index: 1;
    background-color: #f6f6f6;
    text-align: center;
    padding: 0 10px;
    font-size 17px
    font-style normal
    font-weight 400
.login
  display: flex
  z-index: 1
  width 100%
  border-left 8px solid #97ff8c
  +bp(xs s m)
    flex-direction column
  &__video
    width 100%
    height 100%
    object-fit cover
    z-index -1
  &__left
    position relative
    width 496px
    background-size cover !important
    +bp(xs s m)
      position: relative;
      width 100% !important
      height 200px !important
    .logo
      position absolute
      top calc(50% - 125px)
      right calc(-322px/2)
      width 322px
      &--text
        position absolute
        bottom: 80px;
        width: 226px;
        left: 135px;
        +bp(xs s m)
          position absolute
          bottom 20px
          left calc(50% - 226px/2)
          width: 226px;
      +bp(xs s m)
        top calc(50% - 72px/2)
        right calc(50% - 72px/2)
        width 72px
  &__right
    position relative
    flex 1
    display flex
    align-items center
    justify-content center
    overflow scroll
    +bp(xs s m)
      width: 100% !important;
      padding: 0 !important;
      margin-top: 50px;
    .footer
      position: absolute;
      width: 100%;
      padding: 0 0 16px 0;
      bottom: 0px;
      text-align: center;
      background: #ffffff;
      z-index: 1;
      button
        body-b3-medium()
        color var(--Text-4, #747474)
        text-decoration underline
    .login-holder
      width 400px
      display: flex
      justify-content: space-around
      align-items: center;
      animation: 0.3s ease-out 0s 1 scaleUp;
      flex-direction: column
      z-index 1
      padding: 0 50px;
      +bp(xs s m)
        margin-bottom: 50px;
      .logo
        width: 90%;
      .login-options
        display flex
        flex-direction column
        min-height 312px
        margin auto
        position relative
        gap 32px
        transition 1.3s cubic-bezier(0.53, 0, 0.15, 1.3)
        z-index 198
        padding 0px
        box-sizing border-box
        border-radius 5px
        width: 100%;
        padding 0
        .login-practitioner
          display flex
          flex-direction column
          gap 32px
        .header
            background var(--japanese-indigo, #194355)
            padding 10px
            color var(--white, #FFFFFF)
            text-align center
            border-radius 5px 5px 0 0
        .panel-holder
          display flex
          flex-direction row
          flex 1
        .panel
          display flex
          .content
              min-width 360px

          &.psc
            .content
              border-left 1px solid var(--japanese-indigo, #194355)
              .psc-ciba
                padding 20px
                animation: 0.3s ease-out 0.4s 1 fadeIn both
              .psc
                padding-top 20px
                display flex
                border-bottom 1px solid var(--japanese-indigo, #194355)
                animation: 0.3s ease-out 0.3s 1 fadeIn both
          &.epoca
            .content
              animation: 0.3s ease-out 0.2s 1 fadeIn both
              padding 20px
          .content
            display flex
            flex-direction column

@keyframes scaleUp {
    0% {
      transform: scale(0.9, 0.9)
    }
    100% {
      transform: scale(1, 1)
    }
}
@keyframes fadeIn {
    0% {
      opacity 0
      transform: translateY(10px);
    }
    100% {
      opacity 1
      transform: translateY(0);
    }
}
</style>
